import React, { useEffect, useState } from 'react';
import { Avatar } from 'antd';

import { getFileObjectUrl } from '../../services/files';
import { normalizeId } from '../../utils/ids';
import { PERSON } from '../icons';
import LibraryIcon from '../common/LibraryIcon';
import { isLibraryIconName } from '../../utils/libraryIcons';

/**
 * THE user identity mark, everywhere a user appears: pickers, sharing rows,
 * audience reports, the header, the profile page.
 *
 * Renders whichever identity the user chose (users.avatar subdoc):
 *   photo      authenticated fetch of the sized variant -> object URL
 *              (services/files caches per id+variant; ids are immutable)
 *   icon       app icon on the chosen color
 *   (none)     name initial on a neutral ground
 * Fallbacks cascade: a photo that hasn't loaded (or failed) renders like an
 * icon/initial so lists never jump.
 */
export default function UserAvatar({ user, size = 24 }) {
	const avatar = user?.avatar;
	const fileId = avatar?.type === 'photo' ? normalizeId(avatar.fileId) : null;
	// sm is 64px — sharp for anything up to ~2x that; md (256px) beyond.
	const variant = size <= 48 ? 'sm' : 'md';

	const [photoUrl, setPhotoUrl] = useState(null);

	useEffect(() => {
		setPhotoUrl(null);
		if (!fileId) return undefined;
		let active = true;
		getFileObjectUrl(fileId, variant)
			.then(url => { if (active) setPhotoUrl(url); })
			.catch(() => {}); // fall through to the icon/initial rendering
		return () => { active = false; };
	}, [fileId, variant]);

	if (fileId && photoUrl) {
		return <Avatar size={size} src={photoUrl} alt="" />;
	}

	if (avatar?.type === 'icon' && avatar.icon) {
		// A library icon, drawn on demand (KPR-1187). The name is checked as an
		// own key of the library: the server validates its SHAPE, not its
		// membership, and 'constructor' must not resolve through a prototype.
		// A file that fails draws the generic person.
		if (isLibraryIconName(avatar.icon)) {
			return (
				<Avatar
					size={size}
					icon={<LibraryIcon name={avatar.icon} fallback={PERSON} />}
					style={avatar.color ? { backgroundColor: avatar.color } : undefined}
				/>
			);
		}
	}

	const initial = (user?.fullName || user?.firstName || user?.email || '?').trim().charAt(0).toUpperCase();
	const background = avatar?.color ? { backgroundColor: avatar.color } : undefined;
	return (
		<Avatar size={size} style={background}>
			{initial}
		</Avatar>
	);
}
